import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { VisionboardEditorComponent } from '../../herramientas/visionboard-editor/visionboard-editor.component';
import { VisionboardTutorialComponent } from '../../herramientas/visionboard-tutorial/visionboard-tutorial.component';
import { BaseComponent } from '../../../../core/shared/base.component';
import { Visionboard, VisionboardImage, VisionboardDto } from '../../../../core/models/visionboard.interface';
import { AuthService } from '../../../../core/services/auth.service';
import { ConfigurationService } from '../../../../core/services/configuration.service';
import { VisionboardService } from '../../../../core/services/visionboard.service';
import { LoaderService } from '../../../../core/shared/services/loader.service';
import { UsersService } from '../../../../core/services/users.service';

@Component({
  selector: 'app-visionboard-view',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    VisionboardTutorialComponent,
    VisionboardEditorComponent,
  ],
  templateUrl: './visionboard-view.component.html',
  styleUrl: './visionboard-view.component.scss'
})
export class VisionboardViewComponent extends BaseComponent implements OnInit, AfterViewInit {
  private loader = inject(LoaderService);
  private authService: AuthService = inject(AuthService);
  private usersService = inject(UsersService);
  private configService = inject(ConfigurationService);
  private visionboardService = inject(VisionboardService);
  private router = inject(Router);

  showVisionboardTutorial: boolean = false;

  userVisionboards: Visionboard[] = [];
  userId: string | undefined;
  isLoadingVisionboards: boolean = false;
  user: any = null; // Variable para los datos completos del usuario (incluye createdAt)

  currentEditorVisionboardId: string | null = null;
  visionboardImages: VisionboardImage[] = [];
  showingEditorForEmptyVisionboard: boolean = false;

  showVisionboardVideo: boolean = false;
  videoIsMuted: boolean = true;
  currentVideoId: string | null = null;

  // Variables para el menú de opciones y texto
  showOptionsMenu: boolean = false;
  isHidingMenu: boolean = false; // Controla la ocultación visual del menú
  showTextModal: boolean = false;
  showTextEditModal: boolean = false;
  showConfirmModal: boolean = false;
  showClearConfirmModal: boolean = false;
  currentText: string = '';
  currentIndex: number = 0;
  menuPosition = { x: 0, y: 0 };
  private isOpeningFileDialog: boolean = false;

  // Variables para texto avanzado
  currentFontSize: number = 24;
  currentTextColor: string = '#000000';
  currentFontFamily: string = 'Arial';
  
  // Variables para edición de texto
  editingText: any = null;
  textClickPosition: { x: number; y: number } | null = null;
  
  // Variables para debounce de guardado
  private saveTimeout: any = null;
  private isSaving: boolean = false;
  private pendingSave: boolean = false; // Marca si hay un guardado pendiente
  
  // Variable para el estado de carga al finalizar
  isFinishingVisionboard: boolean = false;
  
  // Variables para separar tipos de texto
  showFreeTextModal: boolean = false; // Modal para texto libre en pizarra
  showGridTextModal: boolean = false;
  
  // Variable para controlar la visibilidad del botón finalizar
  showFinishButton: boolean = true;
  
  // Variable para el título del vision board
  visionboardTitle: string = 'Mi Vision Board';

  // Variables para las tareas desbloqueadas
  taskSectionExpanded: boolean = true;
  showIntroMessage: boolean = true;
  currentQuestion: number = 1;
  totalQuestions: number = 5;
  currentAnswer: string = '';
  showCompletionMessage: boolean = false;
  taskAnswers: string[] = [];
  showTaskSummary: boolean = false;
  isVisionBoardComplete: boolean = false;

  // Preguntas para las tareas (público para acceso desde el template)
  questions: string[] = [
    '¿Qué imágenes o frases te generan más emoción?',
    '¿Qué áreas de tu vida están mejor representadas en tu tablero?',
    '¿Qué área quedó menos representada y por qué?',
    '¿Qué sientes al mirar tu Vision Board terminado?',
    '¿De qué manera lo que visualizas en tu Vision Board responde a las brechas identificadas en tu Círculo de la Vida?'
  ];
  textElements: Array<{
    id: string;
    text: string;
    x: number;
    y: number;
    fontSize: number;
    color: string;
    fontFamily: string;
    isDragging: boolean;
    dragOffset: { x: number; y: number };
  }> = [];

  // Referencias a los inputs de archivo
  @ViewChild('fileInput0') fileInput0!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput1') fileInput1!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput2') fileInput2!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput3') fileInput3!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput4') fileInput4!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput5') fileInput5!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput6') fileInput6!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput7') fileInput7!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput8') fileInput8!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput9') fileInput9!: ElementRef<HTMLInputElement>;

  // Referencias para la pizarra interactiva
  @ViewChild('whiteboardCanvas') whiteboardCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('masonryGrid') masonryGrid!: ElementRef<HTMLDivElement>;

  // Variables para la pizarra
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;
  
  // Herramientas de la pizarra
  currentTool: 'pen' | 'highlighter' | 'text' | 'eraser' = 'pen';
  currentColor: string = '#000000';
  brushSize: number = 3;
  whiteboardEnabled: boolean = false;

  // Variables para arrastrar texto
  private isDraggingText = false;
  private draggedTextIndex = -1;
  private dragStartX = 0;
  private dragStartY = 0;
  private backgroundImage: HTMLImageElement | null = null;

  ngOnInit(): void {
    this.currentEditorVisionboardId = null;

    const authUser = this.authService.getAuthUser();
    if (authUser && authUser.id) {
      this.userId = authUser.id;
      this.loadUserData(); // Cargar datos completos del usuario
      this.loadUserVisionboards();
      this.loadExistingTaskAnswers();
      
      // Verificar si el botón finalizar debe mostrarse
      this.checkFinishButtonVisibility();
    } else {
      console.error('HerramientasComponent: Usuario no autenticado.');
      return;
    }
  }

  /**
   * Carga los datos completos del usuario (incluye createdAt)
   */
  private loadUserData(): void {
    if (!this.userId) return;

    this.authService.findUserById(this.userId).subscribe({
      next: (response) => {
        if (response.data) {
          this.user = response.data;
        }
      },
      error: (err) => {
        console.error('Error cargando datos del usuario:', err);
      }
    });
  }

  ngAfterViewInit(): void {
    this.initializeWhiteboard();
    this.startAutoSave();
  }

  ngOnDestroy(): void {
    // Limpiar timeout para evitar memory leaks
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }

  // Métodos para las tareas desbloqueadas
  toggleTaskSection(): void {
    this.taskSectionExpanded = !this.taskSectionExpanded;
  }

  /**
   * Inicia las preguntas ocultando el mensaje introductorio
   */
  startQuestions(): void {
    this.showIntroMessage = false;
  }

  getCurrentQuestion(): string {
    return this.questions[this.currentQuestion - 1] || '';
  }

  nextQuestion(): void {
    if (!this.currentAnswer.trim()) {
      return;
    }

    // Guardar la respuesta actual
    this.taskAnswers[this.currentQuestion - 1] = this.currentAnswer;

    if (this.currentQuestion < this.totalQuestions) {
      // Ir a la siguiente pregunta
      this.currentQuestion++;
      // Cargar la respuesta existente si hay una
      this.currentAnswer = this.taskAnswers[this.currentQuestion - 1] || '';
    } else {
      // Completar todas las preguntas
      this.showCompletionMessage = true;
      this.taskSectionExpanded = false;
    }
  }

  finishTasks(): void {
    if (!this.userId) return;

    // Guardar las respuestas y marcar como completado
    this.saveTaskAnswers();
    
    // Ocultar las preguntas y el mensaje de finalización, mostrar solo el resumen
    this.showCompletionMessage = false;
    this.taskSectionExpanded = false;
    this.showTaskSummary = true;
  }

  private saveTaskAnswers(): void {
    if (!this.userId) return;

    // Crear la estructura de datos para step3
    const step3Data = {
      'stepsAnswers.step3.VisionBoardComplete': true,
      'stepsAnswers.step3.VisionBoardEnable': true,
      'stepsAnswers.step3.VisionBoardReflection': {
        questions: this.questions,
        answers: this.taskAnswers,
        completedAt: new Date(),
        createdAt: new Date()
      },
    };

    console.log('Guardando respuestas de las tareas:', step3Data);

    // Actualizar el usuario con las respuestas y el estado de completado
    this.usersService.updateUser(this.userId, step3Data).subscribe({
      next: () => {
        console.log('Respuestas de Vision Board guardadas exitosamente');
        this.success('¡Tareas completadas exitosamente!');
        
        // Actualizar la variable para mostrar "Tareas desbloqueadas"
        this.isVisionBoardComplete = true;
      },
      error: (err: any) => {
        console.error('Error guardando respuestas de Vision Board:', err);
        this.error('Error al guardar las respuestas. Inténtalo de nuevo.');
      }
    });
  }

  /**
   * Permite editar las respuestas del resumen
   * Vuelve al inicio de la sección para revisar las respuestas
   */
  editTaskAnswers(): void {
    this.showTaskSummary = false;
    this.showCompletionMessage = false;
    this.taskSectionExpanded = true;
    this.showIntroMessage = true; // Mostrar el mensaje introductorio para volver al inicio
    this.currentQuestion = 1;
    this.currentAnswer = this.taskAnswers[0] || '';
  }

  /**
   * Permite editar el Vision Board
   */
  editVisionBoard(): void {
    this.showFinishButton = true;
    this.whiteboardEnabled = false;
  }

  /**
   * Carga las respuestas existentes del usuario para las tareas
   */
  private loadExistingTaskAnswers(): void {
    if (!this.userId) return;

    this.authService.findUserById(this.userId).subscribe({
      next: (response) => {
        if (response.data && response.data.stepsAnswers && response.data.stepsAnswers.step3) {
          const step3Data = response.data.stepsAnswers.step3;
          
          // Verificar si el Vision Board está completado
          this.isVisionBoardComplete = step3Data.VisionBoardComplete === true;
          //this.isVisionBoardComplete = true;
          // Verificar si hay respuestas existentes para VisionBoardReflection
          if (step3Data.VisionBoardReflection && step3Data.VisionBoardReflection.answers) {
            this.taskAnswers = step3Data.VisionBoardReflection.answers;
            
            // Si hay respuestas, no mostrar el mensaje introductorio y mostrar el resumen
            if (this.taskAnswers.length > 0 && this.taskAnswers.some(answer => answer && answer.trim() !== '')) {
              this.showIntroMessage = false;
              this.showTaskSummary = true;
              console.log('Respuestas existentes encontradas, mostrando resumen automáticamente');
            }
          }
        }
      },
      error: (err) => {
        console.error('Error cargando respuestas existentes:', err);
      }
    });
  }

  loadUserVisionboards(): void {
    if (!this.userId) return;
    this.isLoadingVisionboards = true;
    this.visionboardService.getUserVisionboards(this.userId).subscribe({
      next: (response) => {
        this.userVisionboards = response.data || [];
        
        // Inicializar el título del vision board
        if (this.userVisionboards.length > 0 && this.userVisionboards[0].name) {
          this.visionboardTitle = this.userVisionboards[0].name;
        }
        
        // Cargar las imágenes también
        if (this.userId) {
          this.loadUserImages();
        }
        // Cargar datos de la pizarra después de un pequeño delay para asegurar que el canvas esté listo
        setTimeout(() => {
          this.loadWhiteboardData();
        }, 100);
        this.isLoadingVisionboards = false;
      },
      error: (err) => {
        console.error('Error cargando visionboards del usuario:', err);
        this.isLoadingVisionboards = false;
      }
    });
  }

  loadUserImages(): void {
    if (!this.userId) return;
    this.authService.findUserById(this.userId).subscribe({
      next: (response) => {
        if (response.data && response.data.visionboardImages) {
          this.visionboardImages = response.data.visionboardImages;
        }
      },
      error: (err) => {
        console.error('Error cargando imágenes del usuario:', err);
      }
    });
  }

  closeTutorial(): void {
    this.showVisionboardTutorial = false;
  }

  finishTutorial(): void {
    this.showVisionboardTutorial = false;

    // Guardar en localStorage que el tutorial se ha completado
    localStorage.setItem('visionboardTutorialCompleted', 'true');

    // Verificar que se haya guardado correctamente
    const savedValue = localStorage.getItem('visionboardTutorialCompleted');
    console.log('Tutorial completado. Valor guardado en localStorage:', savedValue);

    // Si no se guardó correctamente, intentar de nuevo
    if (savedValue !== 'true') {
      console.warn('El valor no se guardó correctamente en localStorage. Intentando de nuevo...');
      localStorage.setItem('visionboardTutorialCompleted', 'true');
      console.log('Segundo intento, valor en localStorage:', localStorage.getItem('visionboardTutorialCompleted'));
    }
  }

  navigateToEditor(id?: string): void {
    console.log('Navegando al editor con ID:', id);

    // Primero terminar la carga si está en proceso
    if (this.isLoadingVisionboards) {
      setTimeout(() => this.navigateToEditor(id), 500);
      return;
    }

    // Limpiar el editor antes de configurar un nuevo ID
    this.currentEditorVisionboardId = null;

    // Determinar si debe mostrar el tutorial
    const tutorialCompleted = localStorage.getItem('visionboardTutorialCompleted') === 'true';
    console.log('Estado localStorage - Tutorial completado:', tutorialCompleted);
    console.log('Valor exacto en localStorage:', localStorage.getItem('visionboardTutorialCompleted'));

    if (!tutorialCompleted) this.showVisioBoardVideo();

    // Siempre mostrar el tutorial para edición/creación si no ha sido completado antes
    // this.showVisionboardTutorial = !tutorialCompleted;

    // Establecer bandera para indicar si es un Vision Board vacío
    const hasImages = this.visionboardImages && this.visionboardImages.length > 0;
    this.showingEditorForEmptyVisionboard = !hasImages;
    console.log('Info de diagnóstico:');
    console.log('- ¿Mostrar tutorial?:', this.showVisionboardTutorial);
    console.log('- ¿Usuario tiene imágenes?:', hasImages, '(Total:', this.visionboardImages.length, ')');
    console.log('- ¿Es un Vision Board vacío?:', this.showingEditorForEmptyVisionboard);

    // Usar setTimeout para garantizar que el cambio de null sea detectado
    setTimeout(() => {
      // Si tenemos un ID específico, lo usamos; si no, usamos 'new' para indicar creación de nuevo
      this.currentEditorVisionboardId = id || 'new';
      console.log('ID del editor establecido a:', this.currentEditorVisionboardId);
    }, 100);
  }

  onVisionboardSaved(): void {
    // Prevenir múltiples clics
    if (this.isFinishingVisionboard) {
      return;
    }

    // Activar estado de carga
    this.isFinishingVisionboard = true;

    // Actualizar el estado de completado del Vision Board
    if (this.userId) {
      // Obtener el usuario actual para acceder a sus permisos
      this.authService.findUserById(this.userId).subscribe({
        next: (userResponse) => {
          if (userResponse.data) {
            const user = userResponse.data;
            
            // Preparar datos para actualizar
            const updateData = {
              'stepsAnswers.step3.VisionBoardComplete': true
            };

            // Actualizar el usuario con el Vision Board completado
            this.usersService.updateUser(this.userId!, updateData).subscribe({
              next: () => {
                console.log('Vision Board marcado como completado exitosamente');
                
                // Actualizar el estado local
                this.isVisionBoardComplete = true;
                
                // Actualizar el permiso id = 5 a true
                this.updateVisionBoardPermission(user);
                
                // Limpiar el editor actual
                this.currentEditorVisionboardId = null;
                this.showVisionboardTutorial = false;
                this.showingEditorForEmptyVisionboard = false;

                // Recargar primero las imágenes y luego los visionboards
                if (this.userId) {
                  this.authService.findUserById(this.userId).subscribe({
                    next: (response) => {
                      if (response.data && response.data.visionboardImages) {
                        this.visionboardImages = response.data.visionboardImages;
                      }
                      // Después de cargar las imágenes, cargamos los visionboards
                      this.loadUserVisionboards();
                      
                      // Hacer scroll a la sección de Tareas desbloqueadas después de un breve delay
                      // Esperamos un poco más para asegurar que el DOM se haya actualizado
                      setTimeout(() => {
                        this.scrollToTasksSection();
                        this.isFinishingVisionboard = false; // Desactivar estado de carga
                      }, 800);
                    },
                    error: (err) => {
                      console.error('Error recargando imágenes después de guardar:', err);
                      // Aún así, intentamos cargar los visionboards
                      this.loadUserVisionboards();
                      
                      setTimeout(() => {
                        this.scrollToTasksSection();
                        this.isFinishingVisionboard = false; // Desactivar estado de carga
                      }, 800);
                    }
                  });
                } else {
                  this.loadUserVisionboards();
                  setTimeout(() => {
                    this.scrollToTasksSection();
                    this.isFinishingVisionboard = false; // Desactivar estado de carga
                  }, 800);
                }
              },
              error: (err) => {
                console.error('Error marcando Vision Board como completado:', err);
                this.error('Error al marcar el Vision Board como completado');
                this.isFinishingVisionboard = false; // Desactivar estado de carga en caso de error
              }
            });
          } else {
            this.isFinishingVisionboard = false; // Desactivar estado de carga
          }
        },
        error: (err) => {
          console.error('Error obteniendo datos del usuario:', err);
          this.error('Error al obtener datos del usuario');
          this.isFinishingVisionboard = false; // Desactivar estado de carga en caso de error
        }
      });
    } else {
      this.isFinishingVisionboard = false; // Desactivar estado de carga
    }
  }

  /**
   * Hace scroll hasta la sección de Tareas desbloqueadas
   */
  scrollToTasksSection(): void {
    const element = document.getElementById('tareas-desbloqueadas');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // Verificar si ambos flags están en true para ocultar el botón
  private checkFinishButtonVisibility(): void {
    if (this.userId) {
      this.authService.findUserById(this.userId).subscribe({
        next: (userResponse) => {
          if (userResponse.data) {
            const user = userResponse.data;
            
            // Verificar si VisionBoardComplete está en true
            const visionBoardComplete = user.stepsAnswers?.step3?.VisionBoardComplete === true;
            
            // Verificar si el permiso id = 5 está activo
            const permission5Active = user.permissions?.find((p: any) => p.id === "5")?.active === true;
            
            // Si ambos están en true, ocultar el botón
            this.showFinishButton = !(visionBoardComplete && permission5Active);
            
            console.log('Verificación de botón finalizar:', {
              visionBoardComplete,
              permission5Active,
              showFinishButton: this.showFinishButton
            });
          }
        },
        error: (err) => {
          console.error('Error verificando visibilidad del botón:', err);
          // En caso de error, mantener el botón visible
          this.showFinishButton = true;
        }
      });
    }
  }

  // Actualizar el permiso de Vision Board (id = 5)
  private updateVisionBoardPermission(user: any): void {
    if (user.permissions && Array.isArray(user.permissions)) {
      // Buscar el permiso con id "5"
      const permission5 = user.permissions.find((p: any) => p.id === "5");
      
      if (permission5) {
        // Actualizar el permiso localmente
        permission5.active = true;
        permission5.updatedAt = new Date().toISOString();
        
        // Actualizar en el backend
        this.usersService.updatePermissions(this.userId!, user.permissions).subscribe({
          next: () => {
            console.log('Permiso de Vision Board actualizado exitosamente');
            this.success('Vision Board completado exitosamente');
            
            // Actualizar la variable para mostrar "Tareas desbloqueadas"
            this.isVisionBoardComplete = true;
            
            // Ocultar el botón finalizar ya que ambos flags están en true
            this.showFinishButton = false;
          },
          error: (err) => {
            console.error('Error actualizando permiso de Vision Board:', err);
            this.error('Error al actualizar permiso de Vision Board');
          }
        });
      } else {
        console.log('No se encontró el permiso 5 para Vision Board');
        this.success('Vision Board completado exitosamente');
        this.isVisionBoardComplete = true;
      }
    } else {
      console.log('Usuario no tiene permisos definidos');
      this.success('Vision Board completado exitosamente');
      this.isVisionBoardComplete = true;
    }
  }

  // Modificada para aceptar string | undefined
  getPreviewForListItem(imageId: string | undefined): string | null {
    if (!imageId) return null;
    const image = this.visionboardImages.find(img => img.id === imageId);
    return image ? image.imageData : null;
  }

  /**
   * Determina la clase CSS para cada item del collage según su posición
   * @param index Índice de la imagen en el array
   * @param totalImages Total de imágenes disponibles
   * @returns Clase CSS correspondiente al tamaño del item
   */
  getCollageItemClass(index: number, totalImages: number): string {
    // Patrón para crear un diseño de collage visualmente interesante
    if (totalImages <= 1) {
      return 'vb-collage-item--large'; // Si solo hay una imagen, ocupará un espacio grande
    }

    // Patrones para distribución de tamaños en el collage
    if (index === 0) {
      return 'vb-collage-item--large'; // Primera imagen siempre grande
    } else if (index % 5 === 1) {
      return 'vb-collage-item--tall'; // Cada 5 elementos, uno alto
    } else if (index % 7 === 3) {
      return 'vb-collage-item--wide'; // Cada 7 elementos, uno ancho
    } else {
      return ''; // Tamaño normal
    }
  }

  /**
   * Muestra el modal con el video de prueba de autoconocimiento
   */
  showVisioBoardVideo(): void {
    this.loader.show();
    this.configService.getEmbeddedVideos().subscribe({
      next: (response: any) => {
        this.loader.hide();
        if (response?.data && response.data.length > 0) {
          const videoData = response.data.find((video: any) => video.type === 'video_modal_visionboard');

          if (videoData && videoData.url) {
            // Obtener el ID del video
            const videoId = this.extractYoutubeVideoId(videoData.url);
            if (videoId) {
              this.currentVideoId = videoId;
              this.showVideoModal(videoId);
            } else {
              // this.showDefaultAutoconocimientoModal();
            }
          } else {
            // Si no hay video, mostrar el modal con texto
            // this.showDefaultAutoconocimientoModal();
          }
        } else {
          // Si no hay videos configurados, mostrar el modal con texto
          // this.showDefaultAutoconocimientoModal();
        }
      },
      error: (error: any) => {
        console.error('Error al obtener videos embebidos:', error);
        this.loader.hide();
        // En caso de error, mostrar el modal con texto
        // this.showDefaultAutoconocimientoModal();
      }
    });
  }


  /**
   * Muestra el modal con el video y controles de sonido
   */
  showVideoModal(videoId: string): void {
    // Crear estilos para el video y el botón de sonido
    const styles = `
      <style>
        .results-video-container {
          width: 100%;
          display: flex;
          justify-content: center;
        }
        .results-video-wrapper {
          width: 350px;
          height: 350px;
          position: relative;
          overflow: hidden;
          background-color: black;
          border-radius: 0;
        }
        .results-video-iframe {
          position: absolute;
          top: 0%;
          left: 50%;
          width: 100%;
          height: 100%;
          transform: translateX(-50%);
          border: none;
          pointer-events: none;
          object-fit: cover;
        }
        .sound-toggle-btn {
          position: absolute;
          bottom: 55px;
          right: 10px;
          background-color: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 100;
          transition: all 0.2s ease;
        }
        .sound-toggle-btn:hover {
          background-color: rgba(0, 0, 0, 0.9);
          transform: scale(1.1);
        }
        .fullscreen-btn {
          position: absolute;
          bottom: 10px;
          right: 10px;
          background: rgba(0,0,0,0.6);
          border: none;
          border-radius: 50%;
          padding: 8px;
          cursor: pointer;
        }
      </style>
    `;

    // Crear la URL del video con el parámetro mute
    const videoSrc = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&rel=0&showinfo=0&modestbranding=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0&enablejsapi=0&origin=${window.location.origin}&hl=en&cc=0&playsinline=1&autohide=1&color=white&start=1&theme=dark&vq=hd1080&showsearch=0&ecver=2&playerapiid=ytplayer&wmode=transparent&annotation=0`;

    // Crear el HTML del video con el botón de sonido
    const videoHtml = `
      ${styles}
      <div class="results-video-container">
        <div class="results-video-wrapper">
          <iframe 
            id="modal-video-frame"
            class="results-video-iframe"
            src="${videoSrc}" 
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen>
          </iframe>
          <button 
            id="video-sound-toggle" 
            class="sound-toggle-btn" 
            title="Activar sonido">
            <!-- Icono de silencio -->
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M3,9v6h4l5,5V4L7,9H3z" fill="white"/>
              <path d="M16.5,12c0-1.77-1.02-3.29-2.5-4.03v8.05C15.48,15.29,16.5,13.77,16.5,12z M14,3.23v2.06 c2.89,0.86,5,3.54,5,6.71s-2.11,5.85-5,6.71v2.06c4.01-0.91,7-4.49,7-8.77S18.01,4.14,14,3.23z" fill="white" opacity="0.5"/>
              <line x1="23" y1="3" x2="3" y2="21" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <button id="video-fullscreen-toggle" class="fullscreen-btn" title="Pantalla completa">
            <!-- Icono pantalla completa -->
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M7 14H5v5h5v-2H7v-3zm0-4h2V7h3V5H7v5zm10 4h2v3h-3v2h5v-5h-4zm-3-9v2h3v3h2V5h-5z"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Mostrar el modal con video
    this.info(videoHtml,
      "Crea tu Visionboard",
      "Empezar ahora",
      false,
      `<a id="ver-tutorial-link" role="button" style="font-size: 14px; color: #000000; cursor: pointer;">Ver tutorial</a>`,
      this.showTutorial.bind(this));

    // Agregar el evento al botón de sonido después de que el modal se haya mostrado
    setTimeout(() => {
      const soundToggleBtn = document.getElementById('video-sound-toggle');
      if (soundToggleBtn) {
        soundToggleBtn.addEventListener('click', () => this.toggleVideoSound());
      }
      const fullscreenToggleBtn = document.getElementById('video-fullscreen-toggle');
      if (fullscreenToggleBtn) {
        fullscreenToggleBtn.addEventListener('click', () => this.toggleVideoFulscreen());
      }
    }, 100);
  }

  /**
   * Activa o desactiva el sonido del video
   */
  toggleVideoSound(): void {
    this.videoIsMuted = !this.videoIsMuted;

    if (!this.currentVideoId) return;

    const videoFrame = document.getElementById('modal-video-frame') as HTMLIFrameElement;
    const soundToggleBtn = document.getElementById('video-sound-toggle');

    if (videoFrame) {
      // Crear nueva URL con el parámetro mute actualizado
      const newSrc = `https://www.youtube-nocookie.com/embed/${this.currentVideoId}?autoplay=1&mute=${this.videoIsMuted ? '1' : '0'}&controls=0&loop=1&playlist=${this.currentVideoId}&rel=0&showinfo=0&modestbranding=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0&enablejsapi=0&origin=${window.location.origin}&hl=en&cc=0&playsinline=1&autohide=1&color=white&start=1&theme=dark&vq=hd1080&showsearch=0&ecver=2&playerapiid=ytplayer&wmode=transparent&annotation=0`;

      // Actualizar el iframe con la nueva URL
      videoFrame.src = newSrc;
    }

    // Actualizar el botón con el icono correspondiente
    if (soundToggleBtn) {
      soundToggleBtn.innerHTML = this.videoIsMuted ?
        // Icono de silencio
        `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <path d="M3,9v6h4l5,5V4L7,9H3z" fill="white"/>
          <path d="M16.5,12c0-1.77-1.02-3.29-2.5-4.03v8.05C15.48,15.29,16.5,13.77,16.5,12z M14,3.23v2.06 c2.89,0.86,5,3.54,5,6.71s-2.11,5.85-5,6.71v2.06c4.01-0.91,7-4.49,7-8.77S18.01,4.14,14,3.23z" fill="white" opacity="0.5"/>
          <line x1="23" y1="3" x2="3" y2="21" stroke="white" stroke-width="2" stroke-linecap="round"/>
        </svg>` :
        // Icono de sonido activo
        `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <path d="M3,9v6h4l5,5V4L7,9H3z" fill="white"/>
          <path d="M16.5,12c0-1.77-1.02-3.29-2.5-4.03v8.05C15.48,15.29,16.5,13.77,16.5,12z M14,3.23v2.06 c2.89,0.86,5,3.54,5,6.71s-2.11,5.85-5,6.71v2.06c4.01-0.91,7-4.49,7-8.77S18.01,4.14,14,3.23z" fill="white"/>
        </svg>`;

      soundToggleBtn.setAttribute('title', this.videoIsMuted ? 'Activar sonido' : 'Silenciar');
    }
  }

  toggleVideoFulscreen(): void {
    const videoFrame = document.getElementById('modal-video-frame') as HTMLIFrameElement;
    const wrapper = videoFrame?.parentElement;

    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }
    if (wrapper?.requestFullscreen) {
      wrapper.requestFullscreen();
    } else if ((wrapper as any).webkitRequestFullscreen) {
      (wrapper as any).webkitRequestFullscreen();
    } else if ((wrapper as any).mozRequestFullScreen) {
      (wrapper as any).mozRequestFullScreen();
    } else if ((wrapper as any).msRequestFullscreen) {
      (wrapper as any).msRequestFullscreen();
    } else {
      console.warn('Fullscreen API no soportada');
    }
  }

  /**
   * Helper para extraer el ID de un video de YouTube
   */
  private extractYoutubeVideoId(url: string): string | null {
    return this.getYoutubeVideoId(url);
  }

  showTutorial() {
    this.showVisionboardTutorial = true;
  }

  /**
   * Maneja la selección de archivos de imagen
   * @param event El evento del input file
   * @param index El índice de la imagen en el vision board
   */
  onImageSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    
    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      this.error('Por favor selecciona un archivo de imagen válido.');
      return;
    }

    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      this.error('La imagen es demasiado grande. El tamaño máximo es 5MB.');
      return;
    }

    // Convertir la imagen a base64
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const imageData = e.target?.result as string;
      
      if (!imageData) {
        this.error('Error al cargar la imagen. Por favor intenta de nuevo.');
        return;
      }

      // Crear o actualizar el visionboard con la nueva imagen
      this.uploadImageToVisionboard(imageData, index);
    };

    reader.onerror = () => {
      this.error('Error al leer el archivo. Por favor intenta de nuevo.');
    };

    reader.readAsDataURL(file);
  }

  /**
   * Sube una imagen al visionboard
   * @param imageData Los datos de la imagen en base64
   * @param index El índice de la imagen en el vision board
   */
  private uploadImageToVisionboard(imageData: string, index: number): void {
    if (!this.userId) {
      this.error('Usuario no autenticado');
      return;
    }

    this.loader.show();

    // Primero, guardar la imagen en el usuario
    this.authService.findUserById(this.userId).subscribe({
      next: (userResponse) => {
        const currentImages = userResponse.data.visionboardImages || [];
        
        // Crear nueva imagen
        const newImage: VisionboardImage = {
          id: this.generateUniqueId(),
          imageData: imageData
        };

        // Agregar la nueva imagen
        const updatedImages = [...currentImages, newImage];

        // Actualizar el usuario con las nuevas imágenes
        this.usersService.updateUser(this.userId!, { visionboardImages: updatedImages }).subscribe({
          next: () => {
            // Actualizar o crear el visionboard
            this.updateVisionboardWithImage(newImage.id, index);
          },
          error: (err: any) => {
            console.error('Error guardando imagen:', err);
            this.loader.hide();
            this.error('Error al guardar la imagen. Por favor intenta de nuevo.');
          }
        });
      },
      error: (err: any) => {
        console.error('Error obteniendo usuario:', err);
        this.loader.hide();
        this.error('Error al procesar la imagen. Por favor intenta de nuevo.');
      }
    });
  }

  /**
   * Actualiza solo el texto de un elemento del visionboard
   * @param text El texto a guardar
   * @param index El índice del elemento
   */
  private updateVisionboardText(text: string, index: number): void {
    console.log('updateVisionboardText llamado con:', { text, index, userId: this.userId });
    
    if (!this.userId) {
      console.log('No hay userId, saliendo');
      return;
    }

    // Crear un array de 10 posiciones (para el grid de 10 items)
    const totalPositions = 10;
    const images: Array<{ imageId?: string; text?: string }> = [];

    // Si ya existe un visionboard, copiar sus imágenes existentes
    if (this.userVisionboards.length > 0) {
      const visionboard = this.userVisionboards[0];
      const existingImages = visionboard.images || [];
      console.log('Visionboard existente encontrado:', visionboard);
      console.log('Imágenes existentes:', existingImages);
      
      // Inicializar todas las posiciones
      for (let i = 0; i < totalPositions; i++) {
        if (existingImages[i]) {
          images[i] = { 
            imageId: existingImages[i].imageId || '',
            text: existingImages[i].text || ''
          };
        } else {
          images[i] = { imageId: '', text: '' };
        }
      }
      
      // Actualizar solo el texto en la posición específica
      images[index] = { 
        imageId: existingImages[index]?.imageId || '', 
        text: text 
      };

      // Crear el DTO para guardar (con todas las posiciones)
      const visionboardDto: VisionboardDto = {
        id: visionboard.id,
        name: visionboard.name || 'Mi Vision Board',
        images: images.map(img => ({
          imageId: img.imageId || '',
          text: img.text || ''
        }) as any)
      };

      // Actualizar el visionboard
      this.visionboardService.saveVisionboard(this.userId, visionboardDto).subscribe({
        next: () => {
          console.log('Texto actualizado exitosamente');
        },
        error: (err: any) => {
          console.error('Error actualizando texto:', err);
          this.error('Error al actualizar el texto.');
        }
      });
    }
  }

  /**
   * Actualiza el visionboard con la nueva imagen
   * @param imageId El ID de la imagen guardada
   * @param index El índice donde colocar la imagen
   */
  private updateVisionboardWithImage(imageId: string, index: number): void {
    if (!this.userId) return;

    // Crear un array de 10 posiciones (para el grid de 10 items)
    const totalPositions = 10;
    const images: Array<{ imageId?: string; text?: string }> = [];

    // Si ya existe un visionboard, copiar sus imágenes existentes
    if (this.userVisionboards.length > 0) {
      const visionboard = this.userVisionboards[0];
      const existingImages = visionboard.images || [];
      
      // Inicializar todas las posiciones
      for (let i = 0; i < totalPositions; i++) {
        if (existingImages[i]) {
          images[i] = { 
            imageId: existingImages[i].imageId || '',
            text: existingImages[i].text || ''
          };
        } else {
          images[i] = { imageId: '', text: '' };
        }
      }
      
      // Actualizar la posición específica con la nueva imagen (preservar texto existente)
      images[index] = { 
        imageId, 
        text: existingImages[index]?.text || '' 
      };

      // Crear el DTO para guardar (con todas las posiciones)
      const visionboardDto: VisionboardDto = {
        id: visionboard.id,
        name: visionboard.name || 'Mi Vision Board',
        images: images.map(img => ({
          imageId: img.imageId || '',
          text: img.text || ''
        }) as any)
      };

      // Actualizar el visionboard
      this.visionboardService.saveVisionboard(this.userId, visionboardDto).subscribe({
        next: () => {
          this.loader.hide();
          this.success('Imagen agregada exitosamente');
          this.loadUserVisionboards();
        },
        error: (err: any) => {
          console.error('Error actualizando visionboard:', err);
          this.loader.hide();
          this.error('Error al actualizar el vision board.');
        }
      });
    } else {
      // Crear un nuevo visionboard con todas las posiciones vacías excepto la seleccionada
      for (let i = 0; i < totalPositions; i++) {
        if (i === index) {
          images[i] = { imageId, text: '' };
        } else {
          images[i] = { imageId: '', text: '' };
        }
      }

      const newVisionboardDto: VisionboardDto = {
        name: 'Mi Vision Board',
        images: images.map(img => ({
          imageId: img.imageId || '',
          text: img.text || ''
        }) as any)
      };

      this.visionboardService.saveVisionboard(this.userId, newVisionboardDto).subscribe({
        next: () => {
          this.loader.hide();
          this.success('Vision Board creado exitosamente');
          this.loadUserVisionboards();
        },
        error: (err: any) => {
          console.error('Error creando visionboard:', err);
          this.loader.hide();
          this.error('Error al crear el vision board.');
        }
      });
    }
  }

  /**
   * Abre el menú de opciones para un grid item
   */
  openItemOptions(event: MouseEvent, index: number): void {
    console.log('[openItemOptions] Abriendo menú para índice:', index);
    console.log('[openItemOptions] isOpeningFileDialog:', this.isOpeningFileDialog, 'isHidingMenu:', this.isHidingMenu);
    
    // No abrir el menú si estamos en proceso de abrir el diálogo de archivos
    if (this.isOpeningFileDialog || this.isHidingMenu) {
      console.log('[openItemOptions] BLOQUEADO - No se abre el menú porque isOpeningFileDialog o isHidingMenu es true');
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    
    event.stopPropagation();
    this.currentIndex = index;
    
    // Calcular posición del menú
    this.menuPosition = {
      x: event.clientX,
      y: event.clientY
    };
    console.log('[openItemOptions] Posición del menú:', this.menuPosition);
    
    this.showOptionsMenu = true;
    this.isHidingMenu = false;
    console.log('[openItemOptions] showOptionsMenu = true, isHidingMenu = false');
  }

  /**
   * Cierra el menú de opciones al hacer click fuera
   */
  @HostListener('document:click')
  closeOptionsMenu(): void {
    console.log('[closeOptionsMenu] Ejecutado - showOptionsMenu:', this.showOptionsMenu, 'isOpeningFileDialog:', this.isOpeningFileDialog);
    // No cerrar si estamos abriendo el diálogo de archivo
    if (this.isOpeningFileDialog) {
      console.log('[closeOptionsMenu] No se cierra porque isOpeningFileDialog es true');
      return;
    }
    console.log('[closeOptionsMenu] Cerrando menú - showOptionsMenu = false');
    this.showOptionsMenu = false;
  }

  /**
   * Selecciona la opción de imagen
   */
  selectImageOption(event?: Event): void {
    console.log('[selectImageOption] INICIO - showOptionsMenu:', this.showOptionsMenu, 'isHidingMenu:', this.isHidingMenu, 'isOpeningFileDialog:', this.isOpeningFileDialog);
    
    // Prevenir propagación del evento si se proporciona
    if (event) {
      console.log('[selectImageOption] Previniendo propagación del evento');
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Cerrar el menú inmediatamente del DOM (sin animación para evitar problemas visuales)
    console.log('[selectImageOption] Cerrando menú inmediatamente del DOM');
    console.log('[selectImageOption] showOptionsMenu antes:', this.showOptionsMenu);
    this.showOptionsMenu = false;
    this.isHidingMenu = false;
    console.log('[selectImageOption] showOptionsMenu después:', this.showOptionsMenu, 'isHidingMenu:', this.isHidingMenu);
    
    // Marcar que estamos abriendo el diálogo inmediatamente
    console.log('[selectImageOption] Marcando isOpeningFileDialog = true');
    this.isOpeningFileDialog = true;
    
    // Esperar un breve momento para asegurar que el DOM se actualice
    setTimeout(() => {
      console.log('[selectImageOption] Timeout - Abriendo diálogo de archivos');
      // Obtener la referencia del input correspondiente
      const fileInputRefs = [
        this.fileInput0, this.fileInput1, this.fileInput2, this.fileInput3, this.fileInput4,
        this.fileInput5, this.fileInput6, this.fileInput7, this.fileInput8, this.fileInput9
      ];
      
      console.log('[selectImageOption] currentIndex:', this.currentIndex);
      const fileInput = fileInputRefs[this.currentIndex];
      console.log('[selectImageOption] fileInput encontrado:', !!fileInput, 'nativeElement:', !!fileInput?.nativeElement);
      
      if (fileInput && fileInput.nativeElement) {
        console.log('[selectImageOption] Haciendo click en el input de archivo');
        
        // Agregar un listener temporal para prevenir SOLO la propagación (no preventDefault)
        // Esto permite que el input funcione normalmente pero evita que el click se propague al div padre
        const stopPropagation = (e: Event) => {
          console.log('[selectImageOption] Previniendo propagación del click del input (sin preventDefault)');
          e.stopPropagation(); // Solo prevenir propagación, NO preventDefault para que el input funcione
          fileInput.nativeElement.removeEventListener('click', stopPropagation, true);
        };
        
        // Agregar el listener en la fase de captura para interceptar antes de que se propague
        fileInput.nativeElement.addEventListener('click', stopPropagation, true);
        
        // Hacer click en el input
        fileInput.nativeElement.click();
        console.log('[selectImageOption] Click ejecutado en el input');
        
        // Remover el listener después de un breve delay por si acaso
        setTimeout(() => {
          fileInput.nativeElement.removeEventListener('click', stopPropagation, true);
        }, 100);
      } else {
        console.error('[selectImageOption] ERROR: No se encontró el input de archivo para el índice', this.currentIndex);
      }
      
      // Resetear la bandera después de un delay para permitir que el diálogo se abra
      setTimeout(() => {
        console.log('[selectImageOption] Resetando isOpeningFileDialog = false');
        this.isOpeningFileDialog = false;
      }, 500);
    }, 100); // Pequeño delay para asegurar que el DOM se actualice
  }

  /**
   * Selecciona la opción de texto
   */
  selectTextOption(): void {
    this.showOptionsMenu = false;
    
    // Si ya existe texto, precargarlo
    const existingText = this.userVisionboards[0]?.images?.[this.currentIndex]?.text;
    this.currentText = existingText || '';
    
    this.showGridTextModal = true; // Usar modal específico para grid
  }

  selectDeleteOption(): void {
    this.showOptionsMenu = false;
    this.showConfirmModal = true;
  }

  /**
   * Cierra el modal de confirmación
   */
  closeConfirmModal(): void {
    this.showConfirmModal = false;
  }

  /**
   * Confirma la eliminación del elemento
   */
  confirmDelete(): void {
    this.showConfirmModal = false;
    this.deleteGridItem(this.currentIndex);
  }

  /**
   * Elimina el contenido de un elemento del grid
   */
  deleteGridItem(index: number): void {
    if (!this.userVisionboards[0] || index < 0 || index >= this.userVisionboards[0].images.length) {
      return;
    }

    // Limpiar el elemento del grid
    this.userVisionboards[0].images[index] = {
      imageId: '',
      text: ''
    };

    // Actualizar en la base de datos
    this.updateVisionboardWithImage('', index);
    
    // Mostrar mensaje de confirmación
    this.success('Contenido eliminado exitosamente');
  }


  /**
   * Genera un ID único para las imágenes
   */
  private generateUniqueId(): string {
    return `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // ==================== MÉTODOS DE LA PIZARRA INTERACTIVA ====================

  /**
   * Inicializa la pizarra interactiva
   */
  private initializeWhiteboard(): void {
    if (this.whiteboardCanvas && this.masonryGrid) {
      this.canvas = this.whiteboardCanvas.nativeElement;
      this.ctx = this.canvas.getContext('2d')!;
      
      
      // Configurar el canvas para que cubra toda la pizarra
      this.resizeCanvas();
      
      // Configurar el contexto
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.globalCompositeOperation = 'source-over';
      
      // Escuchar cambios de tamaño de ventana
      window.addEventListener('resize', () => this.resizeCanvas());
      
      // Event listener para cambiar el cursor cuando pasa sobre un texto
      this.canvas.addEventListener('mousemove', (e) => {
        if (!this.whiteboardEnabled || this.isDrawing || this.isDraggingText) return;
        
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Solo cambiar cursor si la herramienta de texto está activa
        if (this.currentTool === 'text') {
          const textIndex = this.getTextAtPosition(x, y);
          this.canvas.style.cursor = textIndex >= 0 ? 'grab' : 'crosshair';
        } else {
          // Para otras herramientas, usar cursor de dibujo
          this.canvas.style.cursor = 'crosshair';
        }
      });

      // Event listener para doble clic en texto para editarlo
      this.canvas.addEventListener('dblclick', (e) => {
        if (!this.whiteboardEnabled || this.currentTool !== 'text') return;
        
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const textIndex = this.getTextAtPosition(x, y);
        if (textIndex >= 0) {
          this.openTextEditModal(textIndex);
        }
      });

      // Event listeners para dibujo con mouse
      this.canvas.addEventListener('mousedown', (e) => this.startDrawing(e));
      this.canvas.addEventListener('mousemove', (e) => this.draw(e));
      this.canvas.addEventListener('mouseup', () => this.stopDrawing());
      this.canvas.addEventListener('mouseleave', () => this.stopDrawing());

      // Event listeners para dibujo con touch
      this.canvas.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.startDrawing(e);
      });
      this.canvas.addEventListener('touchmove', (e) => {
        e.preventDefault();
        this.draw(e);
      });
      this.canvas.addEventListener('touchend', (e) => {
        e.preventDefault();
        this.stopDrawing();
      });
    }
  }

  /**
   * Redimensiona el canvas para que cubra toda la pizarra
   */
  private resizeCanvas(): void {
    if (this.canvas && this.masonryGrid) {
      const rect = this.masonryGrid.nativeElement.getBoundingClientRect();
      this.canvas.width = rect.width;
      this.canvas.height = rect.height;
      this.canvas.style.width = rect.width + 'px';
      this.canvas.style.height = rect.height + 'px';
    }
  }

  /**
   * Establece la herramienta actual
   */
  setTool(tool: 'pen' | 'highlighter' | 'text' | 'eraser'): void {
    this.currentTool = tool;
    
    if (this.ctx) {
      switch (tool) {
        case 'pen':
          this.ctx.globalCompositeOperation = 'source-over';
          this.ctx.globalAlpha = 1;
          break;
        case 'highlighter':
          this.ctx.globalCompositeOperation = 'multiply';
          this.ctx.globalAlpha = 0.3;
          break;
        case 'eraser':
          this.ctx.globalCompositeOperation = 'destination-out';
          this.ctx.globalAlpha = 1;
          break;
        case 'text':
          this.ctx.globalCompositeOperation = 'source-over';
          this.ctx.globalAlpha = 1;
          break;
      }
    }
  }

  /**
   * Actualiza el color actual
   */
  updateColor(event: any): void {
    this.currentColor = event.target.value;
    if (this.ctx) {
      this.ctx.strokeStyle = this.currentColor;
      this.ctx.fillStyle = this.currentColor;
    }
  }

  /**
   * Actualiza el tamaño del pincel
   */
  updateBrushSize(event: any): void {
    this.brushSize = parseInt(event.target.value);
    if (this.ctx) {
      this.ctx.lineWidth = this.brushSize;
    }
  }

  /**
   * Activa/desactiva la pizarra
   */
  toggleWhiteboard(): void {
    this.whiteboardEnabled = !this.whiteboardEnabled;
    if (this.canvas) {
      this.canvas.style.pointerEvents = this.whiteboardEnabled ? 'auto' : 'none';
      this.canvas.style.opacity = this.whiteboardEnabled ? '1' : '0.3';
      
      // Si se activa la pizarra, asegurar que no hay fondo blanco
      if (this.whiteboardEnabled) {
        this.clearWhiteboardBackground();
      }
    }
  }

  /**
   * Limpia el fondo blanco de la pizarra sin afectar el contenido dibujado
   */
  private clearWhiteboardBackground(): void {
    if (!this.ctx || !this.canvas) return;
    
    // NO limpiar el canvas para preservar el contenido dibujado
    // Solo redibujar elementos de texto si existen
    if (this.textElements.length > 0) {
      this.drawTextElements();
    }
  }

  /**
   * Limpia toda la pizarra
   */
  clearWhiteboard(): void {
    if (this.ctx && this.canvas) {
      // Limpiar el canvas visualmente
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      
      // Limpiar la imagen de fondo
      this.backgroundImage = null;
      
      // Limpiar todos los elementos de texto
      this.textElements = [];
      
      // Guardar inmediatamente después de limpiar para actualizar la BD
      this.saveWhiteboardData();
      
      console.log('Pizarra limpiada completamente');
    }
  }

  /**
   * Inicia el dibujo
   */
  startDrawing(event: MouseEvent | TouchEvent): void {
    if (!this.whiteboardEnabled) return;
    
    const rect = this.canvas.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    
    this.lastX = clientX - rect.left;
    this.lastY = clientY - rect.top;
    
    // Solo permitir arrastrar texto si la herramienta de texto está activa
    if (this.currentTool === 'text') {
      // Verificar si se hace clic en un texto existente para arrastrarlo
      const clickedTextIndex = this.getTextAtPosition(this.lastX, this.lastY);
      
      if (clickedTextIndex >= 0) {
        // Si se hace clic en un texto, preparar para arrastrar
        this.isDraggingText = true;
        this.draggedTextIndex = clickedTextIndex;
        this.dragStartX = this.lastX;
        this.dragStartY = this.lastY;
        this.canvas.style.cursor = 'grabbing';
        
        // Limpiar completamente el canvas antes de iniciar el arrastre
        this.completelyRedrawCanvas();
        
        event.preventDefault();
      } else {
        // Si no se hace clic en un texto, agregar nuevo texto
        this.addText(this.lastX, this.lastY);
      }
    } else {
      // Para otras herramientas, iniciar dibujo normal
      this.isDrawing = true;
    }
  }

  /**
   * Dibuja en el canvas
   */
  draw(event: MouseEvent | TouchEvent): void {
    if (!this.whiteboardEnabled) return;
    
    const rect = this.canvas.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    
    const currentX = clientX - rect.left;
    const currentY = clientY - rect.top;
    
    // Si se está arrastrando un texto
    if (this.isDraggingText && this.draggedTextIndex >= 0) {
      const deltaX = currentX - this.dragStartX;
      const deltaY = currentY - this.dragStartY;
      
      // Actualizar la posición del texto
      this.textElements[this.draggedTextIndex].x += deltaX;
      this.textElements[this.draggedTextIndex].y += deltaY;
      
      // Limpiar completamente el canvas antes de redibujar
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      // NO establecer fondo blanco para permitir que se vean las imágenes de fondo
      
      // Redibujar eficientemente
      this.redrawCanvasEfficiently();
      
      // Actualizar las coordenadas de inicio para el próximo movimiento
      this.dragStartX = currentX;
      this.dragStartY = currentY;
      
      event.preventDefault();
      return;
    }
    
    // Dibujo normal
    if (!this.isDrawing) return;
    
    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(currentX, currentY);
    this.ctx.strokeStyle = this.currentColor;
    this.ctx.lineWidth = this.brushSize;
    this.ctx.stroke();
    
    this.lastX = currentX;
    this.lastY = currentY;
  }

  /**
   * Detiene el dibujo
   */
  stopDrawing(): void {
    this.isDrawing = false;
    
    // Si se estaba arrastrando un texto, guardar los cambios
    if (this.isDraggingText) {
      this.isDraggingText = false;
      this.draggedTextIndex = -1;
      this.canvas.style.cursor = this.whiteboardEnabled ? 'crosshair' : 'default';
      
      // Limpiar completamente el canvas al final del arrastre
      this.completelyRedrawCanvas();
      
      this.saveWhiteboardData();
    }
  }

  /**
   * Agrega texto en la posición especificada
   */
  private addText(x: number, y: number): void {
    this.currentText = '';
    this.showFreeTextModal = true; // Usar modal específico para texto libre
    this.textClickPosition = { x, y };
  }

  /**
   * Actualiza el tamaño de fuente
   */
  updateFontSize(event: any): void {
    this.currentFontSize = parseInt(event.target.value);
  }

  /**
   * Actualiza el color del texto
   */
  updateTextColor(event: any): void {
    this.currentTextColor = event.target.value;
  }

  /**
   * Actualiza el tamaño de fuente del texto en edición
   */
  updateEditingFontSize(event: any): void {
    this.editingText.fontSize = parseInt(event.target.value);
  }

  /**
   * Actualiza el color del texto en edición
   */
  updateEditingTextColor(event: any): void {
    this.editingText.color = event.target.value;
  }

  /**
   * Guarda el texto libre en la pizarra
   */
  saveFreeText(): void {
    if (!this.currentText.trim()) {
      return;
    }

    // Si no hay posición establecida, usar una posición por defecto
    if (!this.textClickPosition) {
      this.textClickPosition = { x: 100, y: 100 };
    }

    const textElement = {
      id: `text_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: this.currentText,
      x: this.textClickPosition.x,
      y: this.textClickPosition.y,
      fontSize: this.currentFontSize,
      color: this.currentTextColor,
      fontFamily: this.currentFontFamily,
      isDragging: false,
      dragOffset: { x: 0, y: 0 }
    };

    this.textElements.push(textElement);
    
    // Solo dibujar si la pizarra está habilitada y el canvas está disponible
    if (this.whiteboardEnabled && this.ctx && this.canvas) {
      this.drawAllTextElements();
    }
    
    this.closeFreeTextModal();
    this.saveWhiteboardData();
    this.textClickPosition = null; // Resetear después de guardar
  }

  /**
   * Guarda el texto en el elemento del grid
   */
  saveGridText(): void {
    console.log('saveGridText llamado');
    console.log('currentText:', this.currentText);
    console.log('currentIndex:', this.currentIndex);
    console.log('userVisionboards:', this.userVisionboards);
    
    if (!this.currentText.trim()) {
      console.log('Texto vacío, no se guarda');
      return;
    }

    // Guardar texto en el elemento del grid actual
    if (this.userVisionboards[0] && this.currentIndex >= 0) {
      console.log('Guardando texto en posición:', this.currentIndex);
      this.userVisionboards[0].images[this.currentIndex].text = this.currentText;
      
      // Actualizar solo el texto en la base de datos
      this.updateVisionboardText(this.currentText, this.currentIndex);
      
      this.success('Texto guardado exitosamente');
    } else {
      console.log('No se puede guardar - visionboard o índice inválido');
    }
    
    this.closeGridTextModal();
  }

  /**
   * Dibuja todos los elementos de texto en el canvas
   */
  private drawAllTextElements(): void {
    if (!this.ctx || !this.canvas) return;

    // Solo dibujar los textos sin redibujar todo el canvas
    this.drawTextElements();
  }

  /**
   * Redibuja todo el canvas (imagen de fondo + textos)
   */
  private redrawCanvas(): void {
    if (!this.ctx || !this.canvas) return;

    // Limpiar completamente el canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Si hay una imagen de fondo de la pizarra, redibujarla
    const currentVisionboard = this.userVisionboards[0];
    if (currentVisionboard && currentVisionboard.whiteboardData) {
      const img = new Image();
      img.onload = () => {
        // Limpiar nuevamente antes de dibujar la imagen
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.drawImage(img, 0, 0);
        // Después de dibujar la imagen, dibujar los textos
        this.drawTextElements();
      };
      img.src = currentVisionboard.whiteboardData;
    } else {
      // Si no hay imagen de fondo, solo dibujar los textos
      this.drawTextElements();
    }
  }

  /**
   * Dibuja solo los elementos de texto (sin limpiar el canvas)
   */
  private drawTextElements(): void {
    if (!this.ctx) return;

    this.textElements.forEach(textElement => {
      this.ctx.font = `${textElement.fontSize}px ${textElement.fontFamily}`;
      this.ctx.fillStyle = textElement.color;
      this.ctx.fillText(textElement.text, textElement.x, textElement.y);
    });
  }

  /**
   * Fuerza la limpieza completa del canvas
   */
  private forceClearCanvas(): void {
    if (!this.ctx || !this.canvas) return;

    // Guardar el estado actual del contexto
    this.ctx.save();
    
    // Resetear completamente el contexto
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.globalAlpha = 1.0;
    
    // Múltiples métodos de limpieza para asegurar que no quede nada
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    // NO establecer fondo blanco para permitir que se vean las imágenes de fondo
    
    // Limpieza adicional con composición
    this.ctx.globalCompositeOperation = 'destination-out';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.globalCompositeOperation = 'source-over';
    
    // Restaurar el estado del contexto
    this.ctx.restore();
  }

  /**
   * Redibuja el canvas de manera eficiente (sin recargar la imagen de fondo)
   */
  private redrawCanvasEfficiently(): void {
    if (!this.ctx || !this.canvas) return;

    // Estrategia más agresiva: limpiar completamente y redibujar desde cero
    this.completelyRedrawCanvas();
  }

  /**
   * Redibuja completamente el canvas desde cero
   */
  private completelyRedrawCanvas(): void {
    if (!this.ctx || !this.canvas) return;

    // Limpiar completamente el canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    
    // NO establecer fondo blanco para permitir que se vean las imágenes de fondo
    
    // Resetear todas las propiedades del contexto
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.globalAlpha = 1.0;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.shadowColor = 'transparent';
    this.ctx.shadowBlur = 0;
    this.ctx.shadowOffsetX = 0;
    this.ctx.shadowOffsetY = 0;

    // Redibujar imagen de fondo si existe
    if (this.backgroundImage) {
      this.ctx.drawImage(this.backgroundImage, 0, 0);
    }

    // Dibujar todos los textos con configuración limpia
    this.textElements.forEach(textElement => {
      this.ctx.font = `${textElement.fontSize}px ${textElement.fontFamily}`;
      this.ctx.fillStyle = textElement.color;
      this.ctx.globalAlpha = 1.0;
      this.ctx.globalCompositeOperation = 'source-over';
      this.ctx.fillText(textElement.text, textElement.x, textElement.y);
    });
  }

  /**
   * Obtiene el índice del texto en una posición específica
   */
  private getTextAtPosition(x: number, y: number): number {
    if (!this.ctx) return -1;

    // Recorrer los textos en orden inverso (los más recientes primero)
    for (let i = this.textElements.length - 1; i >= 0; i--) {
      const textElement = this.textElements[i];
      
      // Configurar el contexto con la fuente del texto
      this.ctx.font = `${textElement.fontSize}px ${textElement.fontFamily}`;
      
      // Medir el texto
      const metrics = this.ctx.measureText(textElement.text);
      const textWidth = metrics.width;
      const textHeight = textElement.fontSize; // Aproximación de altura
      
      // Verificar si el clic está dentro del área del texto
      // El texto se dibuja desde la línea base, así que y es la parte inferior
      if (
        x >= textElement.x &&
        x <= textElement.x + textWidth &&
        y >= textElement.y - textHeight &&
        y <= textElement.y
      ) {
        return i;
      }
    }
    
    return -1;
  }

  /**
   * Cierra el modal de texto
   */
  closeFreeTextModal(): void {
    this.showFreeTextModal = false;
    this.currentText = '';
    this.textClickPosition = null; // Resetear cuando se cancela
  }

  closeGridTextModal(): void {
    this.showGridTextModal = false;
    this.currentText = '';
  }

  /**
   * Abre el modal de edición de texto
   */
  openTextEditModal(index: number): void {
    const textElement = this.textElements[index];
    if (!textElement) return;

    this.editingText = {
      id: textElement.id,
      text: textElement.text,
      fontSize: textElement.fontSize,
      color: textElement.color,
      fontFamily: textElement.fontFamily,
      index: index
    };

    this.showTextEditModal = true;
  }

  /**
   * Cierra el modal de edición de texto
   */
  closeTextEditModal(): void {
    this.showTextEditModal = false;
    this.editingText = null;
  }

  /**
   * Actualiza el texto existente
   */
  updateText(): void {
    if (!this.editingText) return;

    const textIndex = this.editingText.index;
    if (textIndex >= 0 && textIndex < this.textElements.length) {
      // Mantener la posición original
      const originalX = this.textElements[textIndex].x;
      const originalY = this.textElements[textIndex].y;
      
      this.textElements[textIndex] = {
        id: this.editingText.id,
        text: this.editingText.text,
        x: originalX,
        y: originalY,
        fontSize: this.editingText.fontSize,
        color: this.editingText.color,
        fontFamily: this.editingText.fontFamily,
        isDragging: false,
        dragOffset: { x: 0, y: 0 }
      };
      
      this.redrawCanvas();
      this.saveWhiteboardData();
    }
    this.closeTextEditModal();
  }

  /**
   * Elimina el texto seleccionado
   */
  deleteText(): void {
    if (!this.editingText) return;

    const textIndex = this.editingText.index;
    if (textIndex >= 0 && textIndex < this.textElements.length) {
      this.textElements.splice(textIndex, 1);
      this.redrawCanvas();
      this.saveWhiteboardData();
    }
    this.closeTextEditModal();
  }

  /**
   * Guarda los datos de la pizarra en la base de datos con debounce
   */
  private saveWhiteboardData(): void {
    if (!this.canvas || !this.userId || this.userVisionboards.length === 0) return;
    
    // Si ya hay un guardado en progreso, marcar como pendiente
    if (this.isSaving) {
      console.log('Guardado en progreso, marcando como pendiente...');
      this.pendingSave = true;
      return;
    }

    // Limpiar timeout anterior si existe
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    // Establecer nuevo timeout para debounce (aumentado a 2 segundos)
    this.saveTimeout = setTimeout(() => {
      // Verificar nuevamente antes de ejecutar
      if (!this.isSaving) {
        this.performSave();
      }
    }, 2000); // Esperar 2 segundos antes de guardar
  }

  /**
   * Realiza el guardado real de los datos
   */
  private performSave(): void {
    if (this.isSaving) {
      console.log('Ya hay un guardado en progreso, saltando...');
      return;
    }

    if (!this.canvas || !this.userId || this.userVisionboards.length === 0) {
      console.log('Condiciones no cumplidas para guardar, saltando...');
      return;
    }

    this.isSaving = true;
    this.pendingSave = false; // Limpiar la marca de pendiente
    console.log('Iniciando guardado de pizarra...');

    try {
      // Convertir el canvas a base64
      const whiteboardData = this.canvas.toDataURL('image/png');
      
      // Obtener el visionboard actual
      const currentVisionboard = this.userVisionboards[0];
      
      // Crear el DTO con los datos de la pizarra incluyendo elementos de texto
      const visionboardDto: VisionboardDto = {
        id: currentVisionboard.id,
        name: currentVisionboard.name || 'Mi Vision Board',
        description: currentVisionboard.description || '',
        images: currentVisionboard.images || [],
        whiteboardData: whiteboardData,
        textElements: JSON.stringify(this.textElements) // Guardar elementos de texto como JSON
      };

      // Guardar en la base de datos
      this.visionboardService.saveVisionboard(this.userId || '', visionboardDto).subscribe({
        next: () => {
          console.log('Datos de la pizarra guardados exitosamente');
          this.isSaving = false;
          
          // Si hay un guardado pendiente, ejecutarlo después de un delay
          if (this.pendingSave) {
            console.log('Ejecutando guardado pendiente...');
            setTimeout(() => {
              this.saveWhiteboardData();
            }, 500);
          }
        },
        error: (err: any) => {
          console.error('Error guardando datos de la pizarra:', err);
          this.isSaving = false;
          
          // Si hay un guardado pendiente, ejecutarlo después de un delay
          if (this.pendingSave) {
            console.log('Reintentando guardado pendiente después de error...');
            setTimeout(() => {
              this.saveWhiteboardData();
            }, 1000);
          }
        }
      });
    } catch (error) {
      console.error('Error convirtiendo canvas a base64:', error);
      this.isSaving = false;
    }
  }

  /**
   * Carga los datos de la pizarra desde la base de datos
   */
  private loadWhiteboardData(): void {
    if (!this.canvas || !this.ctx || this.userVisionboards.length === 0) return;

    const currentVisionboard = this.userVisionboards[0];
    
    // Cargar elementos de texto si existen
    if (currentVisionboard.textElements) {
      try {
        this.textElements = JSON.parse(currentVisionboard.textElements);
        // Solo dibujar textos sin limpiar el canvas
        this.drawTextElements();
      } catch (error) {
        console.error('Error cargando elementos de texto:', error);
        this.textElements = [];
      }
    }

    // Cargar imagen de la pizarra si existe (solo si no es un fondo blanco)
    if (currentVisionboard.whiteboardData) {
      try {
        // Crear una imagen desde los datos base64
        const img = new Image();
        img.onload = () => {
          // Verificar si la imagen es solo un fondo blanco
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d')!;
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          // Obtener los datos de píxeles
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const pixels = imageData.data;
          
          // Verificar si todos los píxeles son blancos o transparentes
          let isWhiteBackground = true;
          for (let i = 0; i < pixels.length; i += 4) {
            const r = pixels[i];
            const g = pixels[i + 1];
            const b = pixels[i + 2];
            const a = pixels[i + 3];
            
            // Si hay píxeles que no son blancos o transparentes, no es un fondo blanco
            if (a > 0 && (r < 250 || g < 250 || b < 250)) {
              isWhiteBackground = false;
              break;
            }
          }
          
          if (!isWhiteBackground) {
            // Solo dibujar si no es un fondo blanco
            this.backgroundImage = img;
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            this.ctx.drawImage(img, 0, 0);
          }
          
          // Redibujar elementos de texto
          this.drawTextElements();
        };
        img.onerror = (error) => {
          console.error('Error cargando imagen de la pizarra:', error);
        };
        img.src = currentVisionboard.whiteboardData;
      } catch (error) {
        console.error('Error procesando datos de la pizarra:', error);
      }
    }
  }

  /**
   * Auto-guardado de la pizarra (se ejecuta cada 5 segundos)
   */
  private startAutoSave(): void {
    setInterval(() => {
      if (this.whiteboardEnabled && this.canvas) {
        this.saveWhiteboardData();
      }
    }, 5000); // Guardar cada 5 segundos
  }

  /**
   * Guardado manual de la pizarra (llamado por el usuario)
   */
  saveWhiteboardManually(): void {
    // Limpiar timeout de debounce si existe
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    
    // Guardar inmediatamente
    this.performSave();
    this.success('Pizarra guardada exitosamente');
  }

  /**
   * Descarga un PDF con el contenido completo del Vision Board
   */
  async downloadPDF(): Promise<void> {
    try {
      // Importar las librerías dinámicamente
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).jsPDF;

      // Mostrar mensaje de carga
      this.success('Generando PDF del Vision Board...');

      // Obtener el elemento del contenido específico para PDF (sin toolbar)
      const pdfContent = document.querySelector('.vb-pdf-content') as HTMLElement;
      
      if (!pdfContent) {
        this.error('No se pudo encontrar el contenido del Vision Board');
        return;
      }

      // Preparar el contenido para el PDF
      this.prepareContentForPDF(pdfContent);
      
      // Ocultar elementos vacíos antes de generar el PDF
      this.hideEmptyElementsForPDF(pdfContent);

      // Capturar el contenido como imagen con configuración simplificada
      const canvas = await html2canvas(pdfContent, {
        scale: 2, // Escala más alta para mejor calidad
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: true, // Habilitar logging para debug
        removeContainer: false,
        foreignObjectRendering: false,
        imageTimeout: 30000,
        onclone: (clonedDoc) => {
          // Asegurar que el contenido sea visible en el clon
          const clonedPdfContent = clonedDoc.querySelector('.vb-pdf-content') as HTMLElement;
          if (clonedPdfContent) {
            clonedPdfContent.style.visibility = 'visible';
            clonedPdfContent.style.opacity = '1';
            clonedPdfContent.style.display = 'block';
          }
          
          // Solo restaurar visibilidad de elementos que tienen contenido según los datos del backend
          const gridItems = clonedDoc.querySelectorAll('.vb-grid-item');
          gridItems.forEach((item: Element, index: number) => {
            const gridItem = item as HTMLElement;
            
            // Verificar si el elemento tiene contenido basándose en los datos del backend
            let hasContent = false;
            
            if (this.userVisionboards.length > 0 && index < this.userVisionboards[0].images.length) {
              const imageData = this.userVisionboards[0].images[index];
              // Verificar si tiene imageId válido (no vacío) o texto
              hasContent = !!(imageData.imageId && imageData.imageId.trim() !== '') || 
                          !!(imageData.text && imageData.text.trim() !== '');
            }
            
            // Solo restaurar visibilidad si tiene contenido
            if (hasContent) {
              gridItem.style.visibility = 'visible';
              gridItem.style.opacity = '1';
              gridItem.style.display = '';
              gridItem.style.backgroundColor = '';
              gridItem.style.border = '';
            } else {
              // Los elementos vacíos se camuflan con el fondo blanco
              gridItem.style.setProperty('visibility', 'hidden', 'important');
              gridItem.style.setProperty('opacity', '0', 'important');
              gridItem.style.setProperty('background-color', '#FFFFFF', 'important');
              gridItem.style.setProperty('border', 'none', 'important');
            }
          });
          
          // Asegurar que las imágenes sean visibles
          const images = clonedDoc.querySelectorAll('img');
          images.forEach((img: HTMLImageElement) => {
            img.style.visibility = 'visible';
            img.style.opacity = '1';
            img.style.display = '';
          });
          
          // Asegurar que el canvas sea visible
          const canvas = clonedDoc.querySelector('canvas') as HTMLCanvasElement;
          if (canvas) {
            canvas.style.visibility = 'visible';
            canvas.style.opacity = '1';
            canvas.style.display = 'block';
          }
        }
      });

      // Debug: Verificar dimensiones del canvas
      console.log('Canvas dimensions:', canvas.width, 'x', canvas.height);
      console.log('PDF Content dimensions:', pdfContent.offsetWidth, 'x', pdfContent.offsetHeight);
      
      // Verificar que el canvas no esté vacío
      if (canvas.width === 0 || canvas.height === 0) {
        this.error('Error: El contenido no se pudo capturar correctamente');
        return;
      }

      // Crear el PDF con configuración estándar
      const imgData = canvas.toDataURL('image/png', 1.0);
      
      // Crear PDF en formato A4
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Calcular dimensiones para ajustar al PDF
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Mantener proporción de aspecto
      const aspectRatio = imgWidth / imgHeight;
      const pdfAspectRatio = pdfWidth / pdfHeight;
      
      let finalWidth, finalHeight;
      
      if (aspectRatio > pdfAspectRatio) {
        // La imagen es más ancha que el PDF
        finalWidth = pdfWidth - 20; // Margen de 10mm por lado
        finalHeight = finalWidth / aspectRatio;
      } else {
        // La imagen es más alta que el PDF
        finalHeight = pdfHeight - 20; // Margen de 10mm por lado
        finalWidth = finalHeight * aspectRatio;
      }

      // Centrar la imagen en el PDF
      const x = (pdfWidth - finalWidth) / 2;
      const y = (pdfHeight - finalHeight) / 2;

      // Agregar la imagen al PDF
      pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight);

      // Generar nombre del archivo con fecha
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const fileName = `Vision-Board-${dateStr}.pdf`;

      // Descargar el PDF
      pdf.save(fileName);

      // Restaurar elementos ocultos
      this.restoreElementsAfterPDF(pdfContent);

      // Mostrar mensaje de éxito
      this.success('PDF del Vision Board descargado exitosamente');

    } catch (error) {
      console.error('Error generando PDF:', error);
      this.error('Error al generar el PDF. Inténtalo de nuevo.');
    }
  }

  /**
   * Prepara el contenido para la generación del PDF
   */
  private prepareContentForPDF(pdfContent: HTMLElement): void {
    // Asegurar que todos los estilos estén aplicados
    const style = pdfContent.style;
    
    // Resetear transformaciones que puedan causar deformaciones
    style.transform = 'none';
    style.transformOrigin = 'initial';
    style.position = 'relative';
    style.overflow = 'visible';
    
    // Asegurar que las imágenes estén completamente cargadas
    const images = pdfContent.querySelectorAll('img');
    images.forEach((img: HTMLImageElement) => {
      if (!img.complete) {
        img.style.display = 'none'; // Ocultar imágenes no cargadas
      }
    });
    
    // Asegurar que el canvas de la pizarra esté visible si hay contenido
    const canvas = pdfContent.querySelector('canvas');
    if (canvas && this.whiteboardEnabled) {
      canvas.style.pointerEvents = 'none'; // Desactivar eventos durante la captura
      canvas.style.opacity = '1';
    }
  }

  /**
   * Oculta elementos vacíos para la generación del PDF basándose en los datos del backend
   */
  private hideEmptyElementsForPDF(pdfContent: HTMLElement): void {
    // Obtener los datos del vision board actual
    if (this.userVisionboards.length === 0) return;
    
    const currentVisionboard = this.userVisionboards[0];
    const images = currentVisionboard.images || [];
    
    // Buscar todos los elementos del grid
    const gridItems = pdfContent.querySelectorAll('.vb-grid-item');
    
    gridItems.forEach((item: Element, index: number) => {
      const gridItem = item as HTMLElement;
      
      // Verificar si el elemento tiene contenido basándose en los datos del backend
      let hasContent = false;
      
      if (index < images.length) {
        const imageData = images[index];
        // Verificar si tiene imageId válido (no vacío) o texto
        hasContent = !!(imageData.imageId && imageData.imageId.trim() !== '') || 
                    !!(imageData.text && imageData.text.trim() !== '');
      }
      
      console.log(`Grid item ${index}:`, {
        hasContent,
        imageId: index < images.length ? images[index].imageId : 'N/A',
        text: index < images.length ? images[index].text : 'N/A'
      });
      
      // Si no tiene contenido, ocultarlo visualmente pero mantener el espacio
      if (!hasContent) {
        // Guardar el estado original
        (gridItem as any).__originalVisibility = gridItem.style.visibility || '';
        (gridItem as any).__originalOpacity = gridItem.style.opacity || '';
        (gridItem as any).__originalBackground = gridItem.style.backgroundColor || '';
        (gridItem as any).__originalBorder = gridItem.style.border || '';
        
        // Ocultar visualmente pero mantener el espacio en el layout
        gridItem.style.setProperty('visibility', 'hidden', 'important');
        gridItem.style.setProperty('opacity', '0', 'important');
        gridItem.style.setProperty('background-color', '#FFFFFF', 'important'); // Camuflar con el fondo del PDF
        gridItem.style.setProperty('border', 'none', 'important'); // Quitar bordes para que no se vean
        
        // Ocultar también el contenido interno
        const placeholderContent = gridItem.querySelector('.vb-placeholder-content');
        if (placeholderContent) {
          (placeholderContent as HTMLElement).style.display = 'none';
        }
        
        console.log(`Elemento ${index} ocultado:`, {
          visibility: gridItem.style.visibility,
          opacity: gridItem.style.opacity,
          backgroundColor: gridItem.style.backgroundColor,
          border: gridItem.style.border
        });
      }
    });
  }

  /**
   * Restaura los elementos ocultos después de generar el PDF
   */
  private restoreElementsAfterPDF(pdfContent: HTMLElement): void {
    // Restaurar estilos del contenedor
    const style = pdfContent.style;
    style.transform = '';
    style.transformOrigin = '';
    style.position = '';
    style.overflow = '';
    
    // Restaurar imágenes ocultas
    const images = pdfContent.querySelectorAll('img');
    images.forEach((img: HTMLImageElement) => {
      img.style.display = '';
    });
    
    // Restaurar canvas de la pizarra
    const canvas = pdfContent.querySelector('canvas');
    if (canvas && this.whiteboardEnabled) {
      canvas.style.pointerEvents = 'auto';
      canvas.style.opacity = '1';
    }
    
    // Buscar todos los elementos del grid
    const gridItems = pdfContent.querySelectorAll('.vb-grid-item');
    
    gridItems.forEach((item: Element) => {
      const gridItem = item as HTMLElement;
      
      // Restaurar visibility y opacity originales
      if ((gridItem as any).__originalVisibility !== undefined) {
        gridItem.style.visibility = (gridItem as any).__originalVisibility;
        delete (gridItem as any).__originalVisibility;
      }
      
      if ((gridItem as any).__originalOpacity !== undefined) {
        gridItem.style.opacity = (gridItem as any).__originalOpacity;
        delete (gridItem as any).__originalOpacity;
      }
      
      if ((gridItem as any).__originalBackground !== undefined) {
        gridItem.style.backgroundColor = (gridItem as any).__originalBackground;
        delete (gridItem as any).__originalBackground;
      }
      
      if ((gridItem as any).__originalBorder !== undefined) {
        gridItem.style.border = (gridItem as any).__originalBorder;
        delete (gridItem as any).__originalBorder;
      }
      
      // Restaurar placeholder content si fue ocultado
      const placeholderContent = gridItem.querySelector('.vb-placeholder-content');
      if (placeholderContent && (placeholderContent as HTMLElement).style.display === 'none') {
        (placeholderContent as HTMLElement).style.display = '';
      }
    });
  }

  /**
   * Calcula los días restantes de la semana 5 para completar el vision board
   * La semana 5 va del día 29 al día 35 (7 días)
   * @returns Número de días restantes o null si no estamos en la semana 5
   */
  get daysRemainingWeek5(): number | null {
    if (!this.user?.createdAt) {
      return null;
    }

    const createdAt = new Date(this.user.createdAt);
    const now = new Date();
    
    // Calcular la diferencia en milisegundos
    const diffInMs = now.getTime() - createdAt.getTime();
    
    // Convertir a días (día 1 es el día de creación)
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1;
    
    // Semana 5 va del día 29 al día 35
    const week5StartDay = 29;
    const week5EndDay = 35;
    
    // Si estamos en la semana 5, calcular días restantes
    if (diffInDays >= week5StartDay && diffInDays <= week5EndDay) {
      const daysRemaining = week5EndDay - diffInDays + 1; // +1 para incluir el día actual
      return daysRemaining;
    }
    
    return null;
  }

  /**
   * Obtiene el mensaje de cuenta regresiva para la semana 5
   * @returns Mensaje con los días restantes o mensaje por defecto
   */
  get countdownMessage(): string {
    const daysRemaining = this.daysRemainingWeek5;
    
    if (daysRemaining !== null && daysRemaining > 0) {
      if (daysRemaining === 1) {
        return 'Te queda 1 día para completar tu visión board';
      } else {
        return `Te quedan ${daysRemaining} días para completar tu visión board`;
      }
    }
    
    // Mensaje por defecto si no estamos en la semana 5
    return 'Desde ahora tienes 7 días para completar tu visión board';
  }

  /**
   * Guarda el título del vision board en la base de datos
   */
  saveTitle(): void {
    if (!this.userId || this.userVisionboards.length === 0) {
      return;
    }

    const visionboard = this.userVisionboards[0];
    const visionboardDto: VisionboardDto = {
      id: visionboard.id,
      name: this.visionboardTitle.trim() || 'Mi Vision Board',
      images: visionboard.images || [],
      whiteboardData: visionboard.whiteboardData || '',
      textElements: visionboard.textElements || ''
    };

    this.visionboardService.saveVisionboard(this.userId, visionboardDto).subscribe({
      next: () => {
        console.log('Título actualizado exitosamente');
        // Actualizar el título local
        if (this.userVisionboards.length > 0) {
          this.userVisionboards[0].name = this.visionboardTitle;
        }
      },
      error: (err: any) => {
        console.error('Error actualizando título:', err);
        this.error('Error al actualizar el título.');
      }
    });
  }

  /**
   * Muestra el modal de confirmación para limpiar el visionboard
   */
  showClearVisionboardModal(): void {
    this.showClearConfirmModal = true;
  }

  /**
   * Cierra el modal de confirmación de limpieza
   */
  closeClearConfirmModal(): void {
    this.showClearConfirmModal = false;
  }

  /**
   * Limpia completamente el visionboard y las tareas guardadas
   */
  clearVisionboard(): void {
    if (!this.userId) {
      this.error('Usuario no autenticado.');
      return;
    }

    this.loader.show();
    this.showClearConfirmModal = false;

    // Limpiar el visionboard en la base de datos
    if (this.userVisionboards.length > 0) {
      const visionboard = this.userVisionboards[0];
      
      // Crear un visionboard vacío con 10 posiciones vacías
      const emptyImages: any[] = [];
      for (let i = 0; i < 10; i++) {
        emptyImages.push({ imageId: '', text: '' });
      }

      const emptyVisionboardDto: VisionboardDto = {
        id: visionboard.id,
        name: 'Mi Vision Board',
        description: '',
        images: emptyImages,
        whiteboardData: '',
        textElements: ''
      };

      // Guardar el visionboard vacío
      this.visionboardService.saveVisionboard(this.userId, emptyVisionboardDto).subscribe({
        next: () => {
          // Limpiar las tareas
          this.clearTaskAnswers();
        },
        error: (err: any) => {
          console.error('Error limpiando visionboard:', err);
          this.loader.hide();
          this.error('Error al limpiar el vision board.');
        }
      });
    } else {
      // Si no hay visionboard, solo limpiar las tareas
      this.clearTaskAnswers();
    }
  }

  /**
   * Limpia las respuestas de las tareas
   */
  private clearTaskAnswers(): void {
    if (!this.userId) return;

    const step3Data = {
      'stepsAnswers.step3.VisionBoardComplete': false,
      'stepsAnswers.step3.VisionBoardEnable': false,
      'stepsAnswers.step3.VisionBoardReflection': null
    };

    this.usersService.updateUser(this.userId, step3Data).subscribe({
      next: () => {
        // Limpiar variables locales
        this.clearLocalData();
        
        // Recargar visionboards y datos
        this.loadUserVisionboards();
        this.loadExistingTaskAnswers();
        
        this.loader.hide();
        this.success('Vision board y tareas limpiados exitosamente.');
      },
      error: (err: any) => {
        console.error('Error limpiando tareas:', err);
        this.loader.hide();
        this.error('Error al limpiar las tareas.');
      }
    });
  }

  /**
   * Limpia las variables locales del componente
   */
  private clearLocalData(): void {
    // Limpiar elementos de texto
    this.textElements = [];
    
    // Limpiar respuestas de tareas
    this.taskAnswers = [];
    this.currentAnswer = '';
    this.currentQuestion = 1;
    this.showTaskSummary = false;
    this.showCompletionMessage = false;
    this.isVisionBoardComplete = false;
    this.showIntroMessage = true; // Mostrar mensaje introductorio nuevamente
    
    // Limpiar el canvas
    if (this.canvas && this.ctx) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      // Dibujar fondo blanco
      this.ctx.fillStyle = '#FFFFFF';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }
    
    // Resetear título
    this.visionboardTitle = 'Mi Vision Board';
  }
}
